import { useCallback, useMemo, useState } from 'react';

import { useToast } from '@kanda-libs/ks-design-library';
import { actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';

export interface JobQuoteItemCustomHook {
  isSubmitting: boolean;
  onClick: () => void;
  headingClassName: string;
  buttonClassName: string;
  fileNameClassName: string;
  amountClassName: string;
}

export default function useJobQuoteItemCustom(
  id: string,
  compact: boolean,
): JobQuoteItemCustomHook {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { showError } = useToast();

  const onClick = useCallback(() => {
    setIsSubmitting(true);
    dispatch(
      actions.getDocument({
        params: {
          id,
        },
        onError: () => {
          showError('Error fetching quote - please refresh and try again');
          setIsSubmitting(false);
        },
        onSuccess: (document) => {
          setIsSubmitting(false);
          const targetWindow = window.open('', '_blank');
          const url = `data:${document.mimetype};base64,${document.content}`;
          targetWindow?.document.write(
            `<iframe width="100%" height="100%" src=${url}></iframe>`,
          );
          targetWindow?.focus();
        },
      }),
    );
  }, [id, dispatch, showError]);

  const headingClassName = useMemo(
    () =>
      [
        'col-span-2 -mb-1 text-14-22 text-neutral-700 max-w-full',
        compact ? '' : 'md:-mb-2',
      ].join(' '),
    [compact],
  );

  const buttonClassName = useMemo(
    () =>
      [
        'grid grid-cols-[auto_1fr_auto] gap-x-3 border border-neutral-300 rounded-lg bg-neutral-000 hover:bg-purple-50 trantision-all px-3 py-4 shadow col-span-2',
        compact ? '' : 'md:gap-x-4 md:px-5 md:col-span-1',
      ].join(' '),
    [compact],
  );

  const fileNameClassName = useMemo(
    () =>
      [
        'text-14-22 text-neutral-800 whitespace-normal break-words min-w-0 max-w-full text-left',
        compact ? '' : 'md:text-16-24 ',
      ].join(' '),
    [compact],
  );

  const amountClassName = useMemo(
    () =>
      [
        'text-14-22 text-neutral-900 my-auto text-right col-span-2',
        compact ? '' : 'md:text-16-24 md:col-span-1',
      ].join(' '),
    [compact],
  );

  return {
    isSubmitting,
    onClick,
    headingClassName,
    buttonClassName,
    fileNameClassName,
    amountClassName,
  };
}
